import React from "react";
import TextareaAutosize from "react-textarea-autosize";
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import Text from "@/components/ui/text";
import Input from "@/components/ui/input";
import { Close } from "@dreamhub/designsystem/icons";
import {
  ServiceItemDetail,
  ChargeType
} from "@/pages/settings/pages/products-v2/types";
import { useServiceItemTypes } from "@/pages/settings/pages/products-v2/hooks";
import ContractField from "./components/ContractField";
import { PricingSummary } from "./components/PricingSummary";
import BillingSummary from "./components/BillingSummary";
import ServiceTermsSection from "./components/ServiceTermsSection";
import ServiceDatesSection from "./components/ServiceDatesSection";
import DiscountSection from "./components/DiscountSection";
import BillingScheduleSection from "./components/BillingScheduleSection";
import { useAddServiceForm } from "./hooks/useAddServiceForm";
import { ContractServiceData } from "./types";
import {
  ContractRevenueItem,
  EqPaymentsBillingFrequency
} from "@/pages/contracts/types";
import { CreateRevenueItemRequest } from "@/pages/contracts/hooks";
import SingleCheckbox from "@/components/ui/SingleCheckbox";
import { getCurrencySymbol, getSymbolAdornmentPadding } from "./consts";
import { TermsLineEditability } from "@/pages/contracts/components/updateTerms/types";
import {
  SERVICE_NOTE_MAX_LENGTH,
  SERVICE_NOTE_TEXTAREA_CLASS
} from "@/components/layouts/ServiceNotePopover/ServiceNotePopover";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";

interface AddServiceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedService: ServiceItemDetail | null;
  contractId: string | undefined;
  onAddToContract?: (serviceData: ContractServiceData) => void;
  editingRevenueItem?: ContractRevenueItem | null;
  // Nullable and undefaulted: the summaries below hand this to
  // useCurrencyConversion, whose guard is what suppresses a conversion for a
  // contract whose currency is simply unset.
  currency?: string | null;
  onLocalSubmit?: (
    request: CreateRevenueItemRequest,
    editingRevenueItemId?: string
  ) => void;
  // Update terms only: can close a field the local rules left open, never open one.
  fieldEditability?: TermsLineEditability | null;
  // Update terms: the start dates are fixed once something sets them — an
  // existing line's own value, or an amendment's effective date on a new one.
  lockServiceStartDate?: boolean;
  lockBillingStartDate?: boolean;
  allowServiceEndDateEdit?: boolean;
  defaultServiceStartDate?: string;
  // No copy of this contract is on screen yet to show the row in, so the
  // drawer stays open until the save lands.
  awaitSave?: boolean;
}

const AddServiceDrawer: React.FC<AddServiceDrawerProps> = ({
  isOpen,
  onClose,
  selectedService,
  contractId,
  onAddToContract,
  editingRevenueItem,
  currency,
  onLocalSubmit,
  fieldEditability = null,
  lockServiceStartDate = false,
  lockBillingStartDate = false,
  allowServiceEndDateEdit = false,
  defaultServiceStartDate,
  awaitSave = false
}) => {
  const isBillingFrequencyLocked =
    fieldEditability?.isBillingFrequencyEditable === false;

  const { enabled: showNoteSection } = useFeatureFlag(
    FeatureFlags.CRM_10582_SERVICE_NOTES
  );
  const {
    t,
    selectedPricingId,
    setSelectedPricingId,
    billingStartDate,
    setBillingStartDate,
    isDatePickerOpen,
    setIsDatePickerOpen,
    serviceStartDate,
    setServiceStartDate,
    isServiceDatePickerOpen,
    setIsServiceDatePickerOpen,
    quantityInput,
    setQuantityInput,
    quantity,
    isQuantityInvalid,
    discount,
    setDiscount,
    discountType,
    setDiscountType,
    showDiscountInput,
    setShowDiscountInput,
    customBillingFrequency,
    setCustomBillingFrequency,
    customServiceDuration,
    setCustomServiceDuration,
    setCustomServiceEndDate,
    isCustomServiceEndDatePickerOpen,
    setIsCustomServiceEndDatePickerOpen,
    isCustomDurationSelected,
    isEmptyTermClick,
    setIsEmptyTermClick,
    showOptOutDate,
    setShowOptOutDate,
    optOutDate,
    setOptOutDate,
    isOptOutDatePickerOpen,
    setIsOptOutDatePickerOpen,
    customPrice,
    setCustomPrice,
    showSplitPayment,
    setShowSplitPayment,
    splitPaymentData,
    setSplitPaymentData,
    paymentType,
    setPaymentType,
    scheduledPayments,
    setScheduledPayments,
    autoRenewal,
    setAutoRenewal,
    note,
    setNote,
    isCustomServiceDuration,
    effectivePrice,
    hasRecordedPriceBasis,
    effectiveServiceDuration,
    effectiveBillingFrequency,
    effectiveDurationInMonths,
    calculatedDiscountAmount,
    total,
    serviceEndDate,
    isCustomBillingFrequency,
    isCustomBillingFrequencySelected,
    showBillingFrequencyField,
    pricingOptions,
    billingFrequencyOptions,
    serviceDurationOptions,
    handleAddToContract,
    handleClose,
    isSubmitting,
    isEditMode,
    isFlexiblePricing,
    isFlatRatePricing,
    isPricingPerPeriod,
    isServiceEndDateEditable,
    effectiveServiceItemName,
    hasServiceConfig,
    isServiceStartDateVisible,
    isServiceStartDateLocked,
    isBillingStartDateLocked
  } = useAddServiceForm({
    selectedService,
    contractId,
    onAddToContract,
    onClose,
    editingRevenueItem,
    onLocalSubmit,
    defaultServiceStartDate,
    allowServiceEndDateEdit,
    awaitSave,
    lockServiceStartDate,
    lockBillingStartDate,
    isBillingFrequencyLocked,
    includeNote: showNoteSection
  });

  const { data: serviceItemTypes = [] } = useServiceItemTypes();
  const currencySymbol = getCurrencySymbol(currency);
  const currencyPrefixPadding = getSymbolAdornmentPadding(currencySymbol);

  const isPriceLocked = fieldEditability?.isPriceEditable === false;

  const typeName = serviceItemTypes.find(
    (type) => type.id === selectedService?.typeId
  )?.name;

  if (!selectedService) return null;

  return (
    <Drawer
      open={isOpen}
      direction="right"
      fixed
      modal={false}
      disablePreventScroll={false}
      noBodyStyles={true}
      dismissible={false}
    >
      <AppDrawerContent>
        <DrawerHeader>
          <DrawerTitle>
            <Text variant="lgSubtitle">
              {isEditMode
                ? t("contractTerms.editService")
                : t("contractTerms.addService")}
            </Text>
          </DrawerTitle>
          <IconButton onClick={handleClose} variant="iconNoBg" size="sm">
            <Close />
          </IconButton>
        </DrawerHeader>

        <div className="px-5 py-5 w-full overflow-y-scroll flex-1">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4">
              <div className="flex items-start justify-between w-full">
                <Text variant="body">{effectiveServiceItemName}</Text>
                {typeName && <Text variant="bodySecondary">{typeName}</Text>}
              </div>
              {selectedService.description && (
                <Text variant="bodySecondary">
                  {selectedService.description}
                </Text>
              )}
            </div>

            <div className="border-t-[0.5px] border-border-border1 my-4" />

            {isFlexiblePricing ? null : (
              <ServiceTermsSection
                selectedService={selectedService}
                selectedPricingId={selectedPricingId}
                serviceDuration={effectiveServiceDuration}
                price={effectivePrice}
                billingFrequency={effectiveBillingFrequency}
                pricingOptions={pricingOptions}
                isCustomServiceDuration={isCustomServiceDuration}
                isEmptyTermClick={isEmptyTermClick}
                isFlatRatePricing={isFlatRatePricing}
                onPricingSelect={setSelectedPricingId}
                onEmptyTermClick={() => setIsEmptyTermClick(!isEmptyTermClick)}
              />
            )}

            {isServiceStartDateVisible && (
              <ServiceDatesSection
                serviceStartDate={serviceStartDate}
                serviceEndDate={serviceEndDate}
                isCustomServiceDuration={isCustomServiceDuration}
                customServiceDuration={customServiceDuration}
                serviceDurationOptions={serviceDurationOptions}
                showOptOutDate={showOptOutDate}
                optOutDate={optOutDate}
                isServiceDatePickerOpen={isServiceDatePickerOpen}
                isOptOutDatePickerOpen={isOptOutDatePickerOpen}
                isCustomDurationSelected={isCustomDurationSelected}
                isCustomServiceEndDatePickerOpen={
                  isCustomServiceEndDatePickerOpen
                }
                hideOptOutDate={
                  selectedService.chargeType === ChargeType.ONE_TIME
                }
                isServiceStartDateDisabled={isServiceStartDateLocked}
                isServiceEndDateEditable={isServiceEndDateEditable}
                onServiceStartDateChange={setServiceStartDate}
                onServiceDatePickerToggle={setIsServiceDatePickerOpen}
                onCustomServiceDurationChange={setCustomServiceDuration}
                onShowOptOutDate={() => setShowOptOutDate(true)}
                onOptOutDateChange={setOptOutDate}
                onOptOutDatePickerToggle={setIsOptOutDatePickerOpen}
                onCustomServiceEndDateChange={setCustomServiceEndDate}
                onCustomServiceEndDatePickerToggle={
                  setIsCustomServiceEndDatePickerOpen
                }
              />
            )}

            {hasServiceConfig &&
              selectedService.chargeType !== ChargeType.ONE_TIME && (
                <SingleCheckbox
                  id="autoRenewal"
                  isChecked={autoRenewal}
                  onCheckedChange={() => setAutoRenewal(!autoRenewal)}
                  label={t("contractTerms.autoRenewal")}
                  labelVariant="bodySecondary"
                />
              )}
            {hasServiceConfig && isCustomServiceDuration && (
              <div className="border-t-[0.5px] border-border-border1 my-4" />
            )}

            {hasServiceConfig && isCustomServiceDuration && !isPriceLocked && (
              <ContractField
                label={t(
                  isFlexiblePricing
                    ? "contractTerms.priceFlexible"
                    : isFlatRatePricing
                      ? "contractTerms.priceForEntireServicePeriod"
                      : "contractTerms.pricePerUnitMonth"
                )}
                // A line whose own total supplies a price does not require
                // one to be entered; marking it required leaves an asterisk on
                // a field the user can correctly leave empty.
                required={!isFlexiblePricing && !hasRecordedPriceBasis}
              >
                <div className="relative w-full">
                  <Input
                    type="number"
                    value={customPrice || ""}
                    onChange={(e) =>
                      setCustomPrice(
                        Math.max(0, parseFloat(e.target.value) || 0)
                      )
                    }
                    min={0}
                    step="any"
                    className="w-full"
                    style={{ paddingLeft: currencyPrefixPadding }}
                  />
                  <Text
                    as="span"
                    variant="bodySecondary"
                    className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  >
                    {currencySymbol}
                  </Text>
                </div>
              </ContractField>
            )}

            {hasServiceConfig && !isFlatRatePricing ? (
              <ContractField label={t("contractTerms.quantity")} required>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={quantityInput}
                  onChange={(e) => setQuantityInput(e.target.value)}
                  className="w-full"
                  hasError={isQuantityInvalid}
                />
                {isQuantityInvalid ? (
                  <Text variant="smBody" className="text-red-400 mt-1">
                    {t("contractTerms.quantityMinError")}
                  </Text>
                ) : null}
              </ContractField>
            ) : null}

            {hasServiceConfig && (
              <DiscountSection
                showDiscountInput={showDiscountInput}
                discount={discount}
                discountType={discountType}
                total={total}
                onShowDiscount={() => setShowDiscountInput(true)}
                onDiscountChange={setDiscount}
                onDiscountTypeChange={setDiscountType}
                currency={currency}
              />
            )}

            <PricingSummary
              total={hasServiceConfig ? total : 0}
              quantity={hasServiceConfig ? quantity : 0}
              discount={hasServiceConfig ? discount : 0}
              discountType={discountType}
              discountAmount={hasServiceConfig ? calculatedDiscountAmount : 0}
              unitPrice={hasServiceConfig ? effectivePrice : undefined}
              durationInMonths={
                hasServiceConfig ? effectiveDurationInMonths : undefined
              }
              chargeType={selectedService.chargeType}
              showOnlyTotal={!hasServiceConfig || isPricingPerPeriod}
              currency={currency}
            />

            <div className="border-t-[0.5px] border-border-border1 my-4" />

            <BillingScheduleSection
              selectedService={selectedService}
              billingStartDate={billingStartDate}
              isDatePickerOpen={isDatePickerOpen}
              onBillingStartDateChange={setBillingStartDate}
              onDatePickerToggle={setIsDatePickerOpen}
              customBillingFrequency={customBillingFrequency}
              billingFrequencyOptions={billingFrequencyOptions}
              isCustomBillingFrequencySelected={
                isCustomBillingFrequencySelected
              }
              showBillingFrequencyField={
                showBillingFrequencyField && !isBillingFrequencyLocked
              }
              isBillingStartDateDisabled={isBillingStartDateLocked}
              showSplitPayment={showSplitPayment}
              onShowSplitPayment={() => {
                setShowSplitPayment(true);
                setSplitPaymentData({
                  numberOfPayments: 2,
                  billingFrequencyLabel: t(
                    "contractTerms.billingFrequencyLabels.monthly"
                  ),
                  billingFrequency: EqPaymentsBillingFrequency.MONTHS
                });
              }}
              onCancelSplitPayment={() => {
                setShowSplitPayment(false);
                setSplitPaymentData(null);
                // The split is gone from the drawer, so its rows must not be
                // submitted with the line.
                setPaymentType("equal");
                setScheduledPayments([]);
              }}
              splitPaymentData={splitPaymentData}
              onSplitPaymentDataChange={setSplitPaymentData}
              onCustomBillingFrequencyChange={setCustomBillingFrequency}
              total={total}
              serviceDurationInMonths={effectiveDurationInMonths}
              paymentType={paymentType}
              onPaymentTypeChange={setPaymentType}
              scheduledPayments={scheduledPayments}
              onScheduledPaymentsChange={setScheduledPayments}
              currency={currency}
            />

            {paymentType !== "scheduled" &&
              !(
                selectedService?.chargeType === ChargeType.ONE_TIME &&
                showSplitPayment
              ) && (
                <BillingSummary
                  total={total}
                  serviceDurationInMonths={effectiveDurationInMonths}
                  billingFrequency={effectiveBillingFrequency}
                  billingStartDate={billingStartDate}
                  chargeType={selectedService.chargeType}
                  splitPaymentData={
                    (selectedService.chargeType !== ChargeType.ONE_TIME ||
                      isCustomBillingFrequency ||
                      isCustomServiceDuration) &&
                    isCustomBillingFrequencySelected
                      ? splitPaymentData
                      : null
                  }
                  currency={currency}
                />
              )}

            {showNoteSection && (
              <>
                <div className="border-t-[0.5px] border-border-border1 my-4" />

                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1">
                    <Text variant="body">{t("contractTerms.note.title")}</Text>
                    <Text variant="smBodySecondary">
                      {t("contractTerms.note.subtitle")}
                    </Text>
                  </div>
                  <TextareaAutosize
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    maxLength={SERVICE_NOTE_MAX_LENGTH}
                    minRows={2}
                    placeholder={t("contractTerms.note.placeholder")}
                    className={SERVICE_NOTE_TEXTAREA_CLASS}
                    aria-label={t("contractTerms.note.title")}
                    data-test="add-service-note-input"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <DrawerFooter>
          <div className="flex items-center justify-between p-4 border-t-[0.5px] border-border-border1">
            <div className="flex gap-3 grow items-center justify-end">
              <Button variant="secondaryNoBg" onClick={handleClose}>
                {t("cancel")}
              </Button>
              <Button
                onClick={handleAddToContract}
                disabled={
                  isSubmitting ||
                  !hasServiceConfig ||
                  !billingStartDate ||
                  (isServiceStartDateVisible && !serviceStartDate) ||
                  (isCustomDurationSelected && !serviceEndDate) ||
                  isQuantityInvalid
                }
              >
                {isEditMode
                  ? t("contractTerms.save")
                  : t("contractTerms.addService")}
              </Button>
            </div>
          </div>
        </DrawerFooter>
      </AppDrawerContent>
    </Drawer>
  );
};

export default AddServiceDrawer;
